import { useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCreateHackathon } from '@/lib/hooks/useHackathons';

interface Props {
  batchId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const labelClass = 'mb-1 block text-xs font-medium text-[#6b7280]';

// Full 24h range in 30-minute increments — unlike a single class session
// (ScheduleSessionForm's daytime-only TIME_SLOTS), a hackathon can reasonably
// start or end at any hour of a multi-day event.
const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const mins = i * 30;
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
});

export function HackathonCreateDialog({ batchId, open, onOpenChange }: Props) {
  const [title, setTitle] = useState('');
  const [teamSize, setTeamSize] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const { mutateAsync, isPending } = useCreateHackathon(batchId);

  function reset() {
    setTitle('');
    setTeamSize(1);
    setStartDate('');
    setStartTime('');
    setEndDate('');
    setEndTime('');
  }

  async function handleSubmit() {
    if (!title || !startDate || !startTime || !endDate || !endTime) {
      toast.error('Title, start date/time, and end date/time are required.');
      return;
    }
    try {
      await mutateAsync({
        title,
        team_size: teamSize,
        start_at: new Date(`${startDate}T${startTime}:00`).toISOString(),
        end_at: new Date(`${endDate}T${endTime}:00`).toISOString(),
      });
      toast.success('Hackathon created.');
      onOpenChange(false);
      reset();
    } catch {
      toast.error('Failed to create hackathon.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Hackathon</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <label className={labelClass} htmlFor="hk-title">Title</label>
            <Input
              id="hk-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Arrays & Strings Sprint"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="hk-team-size">Team size (1 = solo)</label>
            <Input
              id="hk-team-size"
              type="number"
              min={1}
              value={teamSize}
              onChange={(e) => setTeamSize(Number(e.target.value))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} htmlFor="hk-start-date">Start date</label>
              <DatePicker value={startDate} onChange={setStartDate} />
            </div>
            <div>
              <label className={labelClass} htmlFor="hk-start-time">Start time</label>
              <Select value={startTime} onValueChange={setStartTime}>
                <SelectTrigger id="hk-start-time">
                  <SelectValue placeholder="Select time…" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass} htmlFor="hk-end-date">End date</label>
              <DatePicker value={endDate} onChange={setEndDate} />
            </div>
            <div>
              <label className={labelClass} htmlFor="hk-end-time">End time</label>
              <Select value={endTime} onValueChange={setEndTime}>
                <SelectTrigger id="hk-end-time">
                  <SelectValue placeholder="Select time…" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
